import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import Map, {
  Layer,
  MapRef,
  NavigationControl,
  Source,
  Marker,
  MarkerDragEvent,
  ViewStateChangeEvent,
  MapEvent,
} from 'react-map-gl/mapbox';
import { GeoJSON } from 'geojson';
import AddIcon from '@mui/icons-material/Add';
import LoopIcon from '@mui/icons-material/Loop';
import { PropertyDetailsResponseData } from '../../models/details.models.tsx';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import MapIcon from '@mui/icons-material/Map';
import { RegridResponse } from '../../models/regrid.model.tsx';
import { SetbackSource } from './components/visualizations/setback-visualization.tsx';
import { useWetlandsData } from './hooks/use-wetlands.ts';
import { WetlandsSource } from './components/visualizations/wetlands-visualization.tsx';
import { FloodzoneVisualization } from './components/visualizations/floodzone-visualization.tsx';
import {
  MAP_STARTING_LATITUDE,
  MAP_STARTING_LONGITUDE,
} from '../../possibly-stateful-components/Map/MapboxMap.tsx';
import { HomeModelsResponseData } from '../../models/homes.models.tsx';
import { useGetTopographyData } from '../../api/topography.ts';
import { TopographySource } from './components/visualizations/topography-visualization.tsx';
import { PostHomePlacementRequest } from '../../models/home-placement.models.tsx';
import { ToastContainer, toast } from 'react-toastify';
import { useGetCurrentUser } from '../../hooks/auth.ts';
import { useMutateHomePlacement } from '../../api/details.ts';
import { useUploadImageHomeDesign } from '../../api/homes.ts';
import { useGetFloodData } from '../../api/floodzone.ts';

import { HomeModelPlacementToast } from './components/home-model-placement-toast/home-model-placement-toast.tsx';
import { fit } from '@cloudinary/url-gen/actions/resize';
import { useHomePlacementValidation } from './hooks/use-home-placement-validation.ts';
import { useHomeModelCorners } from './hooks/use-home-model-corners.ts';
import { useHomeRotation } from './hooks/use-home-rotation.ts';
import { useMapViewState } from './hooks/use-map-view-state.ts';
import {
  eAndMDeepwaterStripeImage,
  eAndMWetlandStripeImage,
  freshwaterEmergentWetlandStripeImage,
  freshwaterForestedShrubStripeImage,
  freshwaterPondStripeImage,
  lakeStripeImage,
  riverineStripeImage,
  otherStripeImage,
} from './constants/2d-map-images.ts';
import { HomePlacement, LotSideIndexes, Position } from './types/position.ts';
import { devOnlyCloudinaryInstance } from '../../utils/images.tsx';
import {
  calculatePixelSizeFromMeters,
  getAngleBetweenThreePoints,
  getSetbackCoordinates,
} from './utils/coords-math.ts';

interface LandVisualizationMapProps {
  addressDetails?: PropertyDetailsResponseData;
  regridData?: RegridResponse;
  selectedOverlay?: string;
  selectedHomeObject: HomeModelsResponseData | null;
  selectedBuilderViewLayers: string[];
  homePlacement: HomePlacement;
  setHomePlacement: Dispatch<SetStateAction<HomePlacement>>;
  mapViewType: string;
  setMapViewType: Dispatch<SetStateAction<string>>;
}

export const TwoDimensionHomeViewer = ({
  addressDetails,
  regridData,
  selectedOverlay,
  selectedBuilderViewLayers,
  selectedHomeObject,
  homePlacement,
  setHomePlacement,
  mapViewType,
  setMapViewType,
}: LandVisualizationMapProps) => {
  const mapRef = useRef<MapRef>(null);

  const { viewState, handleViewStateChange } = useMapViewState({
    addressDetails,
    mapRef,
    initialLongitude: MAP_STARTING_LONGITUDE,
    initialLatitude: MAP_STARTING_LATITUDE,
    initialZoom: 19,
  });

  const [movingHomePlacement, setMovingHomePlacement] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [savedName, setSavedName] = useState('');
  const [lotSideIndexes, setLotSideIndexes] = useState({
    frontSideIndex: -1,
    backSideIndex: -1,
  } as LotSideIndexes);

  const { data: wetlandsData, isPending: wetlandsDataLoading } =
    useWetlandsData(
      parseFloat(regridData?.locationInformation.latitude || '0'),
      parseFloat(regridData?.locationInformation.longitude || '0')
    );

  const {
    data: floodZoneData,
    loading: floodZoneDataLoading,
    error: floodZoneDataError,
  } = useGetFloodData(
    parseFloat(regridData?.locationInformation.latitude || '0'),
    parseFloat(regridData?.locationInformation.longitude || '0')
  );

  const { data: topographyData, loading: topographyLoading } =
    useGetTopographyData(addressDetails?.latitude, addressDetails?.longitude);

  const { data: user } = useGetCurrentUser();

  const coordinates = useMemo(() => {
    return [...(regridData?.parcelData.geometry.coordinates[0] || [])];
  }, [regridData?.parcelData.geometry.coordinates]);

  const validVertices = useMemo(() => {
    if (!coordinates?.length) return [];

    return coordinates.filter((_coor, index) => {
      let secondIndex = index === coordinates.length - 1 ? 0 : index + 1;
      let thirdIndex = index === 0 ? coordinates.length - 1 : index - 1;

      const pointA = coordinates[secondIndex];
      const pointB = coordinates[index];
      const pointC = coordinates[thirdIndex];

      const aEqualsB = pointA[0] === pointB[0] && pointA[1] === pointB[1];
      let bEqualsC = pointC[0] === pointB[0] && pointC[1] === pointB[1];

      if (index == 0) {
        if (aEqualsB) {
          secondIndex += 1;
          coordinates[coordinates.length - 1] = coordinates[1];
        } else if (bEqualsC) {
          thirdIndex -= 1;
          if (
            getAngleBetweenThreePoints(
              coordinates[secondIndex],
              coordinates[index],
              coordinates[thirdIndex]
            ) > 150
          ) {
            coordinates[coordinates.length - 1] = coordinates[1];
          }
          bEqualsC = false;
        }
      }

      return (
        aEqualsB ||
        bEqualsC ||
        getAngleBetweenThreePoints(
          coordinates[secondIndex],
          coordinates[index],
          coordinates[thirdIndex]
        ) < 150
      );
    });
  }, [coordinates]);

  const [homeModelSize, setHomeModelSize] = useState({
    height: 0,
    width: 0,
  });

  const homePlacementLatitude = homePlacement.latitude;
  const homePlacementLongitude = homePlacement.longitude;
  const homePlacementRotation = homePlacement.rotation;

  const setHomePlacementRotation = useCallback(
    (rotation: number) => {
      setHomePlacement((v) => ({ ...v, rotation: rotation }));
    },
    [setHomePlacement]
  );
  const setHomePlacementLatitude = useCallback(
    (latitude: number) => {
      setHomePlacement((v) => ({ ...v, latitude: latitude }));
    },
    [setHomePlacement]
  );
  const setHomePlacementLongitude = useCallback(
    (longitude: number) => {
      setHomePlacement((v) => ({ ...v, longitude: longitude }));
    },
    [setHomePlacement]
  );

  const { homeModelCornerCoordinates } = useHomeModelCorners(
    useMemo(
      () => ({
        selectedHomeObjectWidth: selectedHomeObject?.width,
        selectedHomeObjectLength: selectedHomeObject?.length,
        homePlacementLatitude,
        homePlacementLongitude,
        homePlacementRotation,
      }),
      [
        homePlacementLatitude,
        homePlacementLongitude,
        homePlacementRotation,
        selectedHomeObject,
      ]
    )
  );

  const homeModelPlacementError = useHomePlacementValidation(
    useMemo(
      () => ({
        homeModelCornerCoordinates,
        regridData,
        wetlandsData,
        floodZoneData,
        lotSideIndexes,
        validVertices,
      }),
      [
        floodZoneData,
        homeModelCornerCoordinates,
        lotSideIndexes,
        regridData,
        validVertices,
        wetlandsData,
      ]
    )
  );

  const {
    rotatingHomePlacement,
    handleRotateStart,
    handleRotateMove,
    handleRotateEnd,
  } = useHomeRotation({
    homePlacementRotation,
    setHomePlacementRotation,
    rotationSensitivity: 1.0,
  });

  const handleMapViewChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newMapView: string | null) => {
      if (newMapView) {
        setMapViewType(newMapView);
      }
    },
    [setMapViewType]
  );

  const homeModelRef = useRef(null as null | HTMLElement);

  const updateHomeModelSize = useCallback(
    (homeModelRefEl: HTMLElement) => {
      if (!homeModelRefEl || !selectedHomeObject || !addressDetails) return;

      const homeModelWidthInMeters = (selectedHomeObject?.width || 0) / 3.28;
      const homeModelHeightInMeters = (selectedHomeObject?.length || 0) / 3.28;

      const widthPixelSize = calculatePixelSizeFromMeters(
        homeModelWidthInMeters,
        mapRef,
        addressDetails
      );

      const heightPixelSize = calculatePixelSizeFromMeters(
        homeModelHeightInMeters,
        mapRef,
        addressDetails
      );

      setHomeModelSize({
        width: widthPixelSize || 0,
        height: heightPixelSize || 0,
      });
    },
    [selectedHomeObject, addressDetails]
  );

  const divImgRefCB = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element) return;
      updateHomeModelSize(element);
      homeModelRef.current = element;
    },
    [updateHomeModelSize]
  );

  const handleZoom = useCallback(() => {
    const container = homeModelRef.current;
    if (!container) return;
    updateHomeModelSize(container);
  }, [updateHomeModelSize]);

  const handleMoveStart = useCallback(() => {
    setMovingHomePlacement(true);
  }, []);

  const handleMoveEnd = useCallback(() => {
    setMovingHomePlacement(false);
  }, []);

  const showingOverlayInBuiderView = useCallback(
    (overlayToShow: string) => {
      return (
        selectedOverlay === 'Builder View' &&
        selectedBuilderViewLayers.includes(overlayToShow)
      );
    },
    [selectedOverlay, selectedBuilderViewLayers]
  );

  const shouldShowSetbacks = useMemo(() => {
    return (
      (selectedOverlay === 'Setbacks' ||
        showingOverlayInBuiderView('Setbacks')) &&
      regridData
    );
  }, [regridData, selectedOverlay, showingOverlayInBuiderView]);

  const shouldShowTopography = useMemo(() => {
    return (
      (selectedOverlay === 'Topography' ||
        showingOverlayInBuiderView('Topography')) &&
      regridData
    );
  }, [regridData, selectedOverlay, showingOverlayInBuiderView]);

  const shouldShowWetlands = useMemo(() => {
    return (
      selectedOverlay === 'Wetlands' || showingOverlayInBuiderView('Wetlands')
    );
  }, [selectedOverlay, showingOverlayInBuiderView]);

  const shouldShowFloodzone = useMemo(() => {
    return (
      (selectedOverlay === 'Flood Zone' ||
        showingOverlayInBuiderView('Flood Zone')) &&
      addressDetails &&
      regridData
    );
  }, [addressDetails, regridData, selectedOverlay, showingOverlayInBuiderView]);

  const shouldShowHomePlacement = useMemo(() => {
    return (
      selectedHomeObject?.home_placement_model_2d_id &&
      (selectedOverlay === 'Home Placement' ||
        showingOverlayInBuiderView('Home Placement'))
    );
  }, [selectedHomeObject, selectedOverlay, showingOverlayInBuiderView]);

  const shouldShowStripesOnWetlands = useMemo(() => {
    return (
      selectedOverlay === 'Builder View' &&
      selectedBuilderViewLayers.includes('Wetlands') &&
      selectedBuilderViewLayers.includes('Flood Zone')
    );
  }, [selectedBuilderViewLayers, selectedOverlay]);

  const onMarkerDrag = useCallback(
    (event: MarkerDragEvent) => {
      setHomePlacementLatitude(event.lngLat.lat);
      setHomePlacementLongitude(event.lngLat.lng);
    },
    [setHomePlacementLatitude, setHomePlacementLongitude]
  );

  const { mutate: useUploadImageMutate } = useUploadImageHomeDesign();
  const usePostHomePlacement = useMutateHomePlacement();

  const handleSaveHomeOnLand = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalSave = useCallback(() => {
    const setbackCoordinates = getSetbackCoordinates(
      validVertices || [],
      lotSideIndexes,
      regridData
    );
    let homePlacementImageIdResponse: string = '';
    const map = mapRef.current?.getMap();
    if (map) {
      const canvas = map.getCanvas();
      canvas.toBlob((blob) => {
        if (blob) {
          const formData = new FormData();
          formData.append('file', blob, `${user?.userId}/${savedName}.png`);
          formData.append('upload_preset', 'cjkgrd51');
          formData.append('tags', `${user?.userId}-home-on-land`);
          formData.append(
            'metadata',
            `customer_id=${user?.userId}|priority_rank=${1}`
          );
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useUploadImageMutate(formData, {
            onSuccess: (data) => {
              homePlacementImageIdResponse = data.public_id;
              const saveHomePlacementRequest: PostHomePlacementRequest = {
                address: addressDetails?.address,
                homeModelId: selectedHomeObject?.id || 0,
                homeModelName: selectedHomeObject?.name || '',
                homePlacementImageId: homePlacementImageIdResponse,
                rotation: homePlacementRotation,
                latitude: homePlacementLatitude,
                longitude: homePlacementLongitude,
                parcelBoundary: {
                  type: 'FeatureCollection',
                  features: [
                    {
                      type: 'Feature',
                      properties: { name: 'parcelBoundary' },
                      id: 1,
                      geometry: {
                        type: 'Polygon',
                        coordinates:
                          regridData?.parcelData.geometry.coordinates || [],
                      },
                    },
                  ],
                },
                selectedSetbacks: {
                  type: 'FeatureCollection',
                  features: [
                    {
                      type: 'Feature',
                      properties: { name: 'selectedSetbacks' },
                      id: 1,
                      geometry: {
                        type: 'Polygon',
                        coordinates: [setbackCoordinates],
                      },
                    },
                  ],
                },
                savedName: savedName,
              };
              usePostHomePlacement.mutate(saveHomePlacementRequest, {
                onSuccess: () => {
                  toast.success('Home placement saved successfully.');
                  console.log('Uploaded image data:', data);
                },
                onError: () => {
                  toast.error('Failed to save home placement.');
                },
              });
            },
            onError: (error) => {
              toast.error('Failed to upload image.');
              console.error('Upload error:', error);
            },
          });
        }
      }, 'image/png');
    }

    setIsModalOpen(false);
  }, [
    addressDetails,
    homePlacementLatitude,
    homePlacementLongitude,
    homePlacementRotation,
    lotSideIndexes,
    regridData,
    savedName,
    selectedHomeObject,
    usePostHomePlacement,
    useUploadImageMutate,
    user,
    validVertices,
  ]);

  const onMove = useCallback(
    (evt: ViewStateChangeEvent) => {
      handleViewStateChange(evt);
    },
    [handleViewStateChange]
  );

  const onLoad = useCallback((e: MapEvent) => {
    e.target.addImage(
      'stripe-pattern Estuarine and Marine Deepwater',
      eAndMDeepwaterStripeImage
    );
    e.target.addImage(
      'stripe-pattern Estuarine and Marine Wetland',
      eAndMWetlandStripeImage
    );
    e.target.addImage(
      'stripe-pattern Freshwater Emergent Wetland',
      freshwaterEmergentWetlandStripeImage
    );
    e.target.addImage(
      'stripe-pattern Freshwater Forested/Shrub Wetland',
      freshwaterForestedShrubStripeImage
    );
    e.target.addImage(
      'stripe-pattern Freshwater Pond',
      freshwaterPondStripeImage
    );
    e.target.addImage('stripe-pattern Lake', lakeStripeImage);
    e.target.addImage('stripe-pattern Riverine', riverineStripeImage);
    e.target.addImage('stripe-pattern Other', otherStripeImage);
  }, []);

  const style = useMemo(
    () => ({
      width: '100%',
      height: '100%',
    }),
    []
  );

  const resetSetbacks = useCallback(() => {
    setLotSideIndexes({
      frontSideIndex: -1,
      backSideIndex: -1,
    });
  }, []);

  const divImgRefStyle = useMemo(
    () =>
      ({
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        width: `${homeModelSize.width}px`,
        height: `${homeModelSize.height}px`,
      }) as const,
    [homeModelSize]
  );

  const homeModelImgSrc = useMemo(() => {
    if (!selectedHomeObject) return '';
    return devOnlyCloudinaryInstance
      .image(selectedHomeObject.home_placement_model_2d_id)
      .resize(
        fit()
          .width(Math.round(homeModelSize.width))
          .height(Math.round(homeModelSize.height))
      )
      .toURL();
  }, [homeModelSize.height, homeModelSize.width, selectedHomeObject]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const mapStyle = useMemo(() => {
    return mapViewType === 'map'
      ? 'mapbox://styles/mapbox/light-v9'
      : 'mapbox://styles/mapbox/satellite-v9';
  }, [mapViewType]);

  const geoJsonData = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: regridData?.parcelData.geometry
              .coordinates as Position[][],
          },
        },
      ],
    } satisfies GeoJSON;
  }, [regridData]);

  const geoPaint = useMemo(() => {
    return {
      'line-opacity': 0.9,
      'line-width': 3,
      'line-color': '#E99B7A',
    };
  }, []);

  const onDragEnd = useCallback(
    (e: MarkerDragEvent) => {
      onMarkerDrag(e);
      setMovingHomePlacement(false);
      handleRotateEnd();
    },
    [handleRotateEnd, onMarkerDrag]
  );

  const searchSx = useMemo(() => {
    return {
      padding: '0',
      width: '40%',
      height: '40%',
      left: '-45%',
      position: 'absolute',
      top: '25%',
    };
  }, []);

  const addIconSx = useMemo(() => {
    return { color: 'var(--brand-primary)', width: '100%', height: '100%' };
  }, []);

  const rotateButtonSx = useMemo(() => {
    return {
      padding: '0',
      width: '40%',
      height: '40%',
      right: '-45%',
      position: 'absolute',
      top: '25%',
    };
  }, []);

  const rotateIconSx = useMemo(() => {
    return { color: 'var(--brand-primary)', width: '100%', height: '100%' };
  }, []);

  const transformNoneSx = useMemo(() => {
    return { textTransform: 'none' };
  }, []);

  const onSavedNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSavedName(e.target.value);
  }, []);

  return (
    <div
      className="mapbox-map land-visualization-map"
      data-testid="mapbox-map-wrapper"
      onMouseMove={handleRotateMove}
      onMouseUp={handleRotateEnd}
    >
      <div className="map-container">
        <Map
          preserveDrawingBuffer={true}
          data-mcm-mapbox-map
          longitude={viewState.longitude}
          latitude={viewState.latitude}
          zoom={viewState.zoom}
          mapboxAccessToken={import.meta.env.VITE_MAPBOX_PUBLIC_KEY as string}
          dragPan={!movingHomePlacement && !rotatingHomePlacement}
          dragRotate={false}
          onMove={onMove}
          onLoad={onLoad}
          style={style}
          ref={mapRef}
          onZoom={handleZoom}
          pitch={0}
          bearing={0}
          mapStyle={mapStyle}
          projection={'equirectangular'}
        >
          <ToggleButtonGroup
            className="map-view-toggle"
            data-map-view-toggle
            value={mapViewType}
            exclusive
            onChange={handleMapViewChange}
            aria-label="text alignment"
          >
            <ToggleButton
              value="satellite"
              aria-label="left aligned"
              data-sat-view-button
            >
              <LanguageIcon />
              <span>Sat</span>
            </ToggleButton>
            <ToggleButton
              value="map"
              aria-label="centered"
              data-map-view-button
            >
              <MapIcon />
              <span>Map</span>
            </ToggleButton>
          </ToggleButtonGroup>

          <NavigationControl
            showZoom={true}
            showCompass
            position="bottom-right"
          />

          {(lotSideIndexes.frontSideIndex !== -1 ||
            lotSideIndexes.backSideIndex !== -1) && (
            <Button
              variant="contained"
              className={'reset-setbacks'}
              onClick={resetSetbacks}
            >
              Reset Setbacks
            </Button>
          )}

          {regridData?.parcelData.geometry.coordinates && (
            <Source type="geojson" data={geoJsonData}>
              <Layer id="data" type="line" paint={geoPaint} />
            </Source>
          )}
          {shouldShowSetbacks && (
            <>
              <SetbackSource
                lotSideIndexes={lotSideIndexes}
                setLotSideIndexes={setLotSideIndexes}
                validVertices={validVertices}
                mapType={mapViewType || ''}
                regridData={regridData}
                zoom={viewState.zoom}
              />
            </>
          )}
          {shouldShowTopography && (
            <>
              <TopographySource
                topographyData={topographyData?.features || []}
                loading={topographyLoading}
              />
            </>
          )}
          {shouldShowWetlands && (
            <>
              <WetlandsSource
                wetlandsData={wetlandsData || { features: [] }}
                wetlandsDataLoading={wetlandsDataLoading}
                builderViewSelected={shouldShowStripesOnWetlands}
              />
            </>
          )}
          {shouldShowFloodzone && (
            <FloodzoneVisualization
              floodZoneType={regridData?.floodZoneData.femaFloodZone || ''}
              floodZoneData={floodZoneData}
              loading={floodZoneDataLoading}
              error={floodZoneDataError}
            />
          )}
          {shouldShowHomePlacement && (
            <Marker
              latitude={homePlacementLatitude || 0}
              longitude={homePlacementLongitude || 0}
              draggable={movingHomePlacement}
              onDragEnd={onDragEnd}
              onDrag={onMarkerDrag}
              rotation={homePlacementRotation}
            >
              <div ref={divImgRefCB} style={divImgRefStyle}>
                <img
                  src={homeModelImgSrc}
                  alt="Home Placement"
                  className="home-placement-image"
                />
                <div className="search-icon-container">
                  <IconButton
                    onMouseDown={handleMoveStart}
                    onMouseUp={handleMoveEnd}
                    sx={searchSx}
                    aria-label="search"
                  >
                    <AddIcon sx={addIconSx} />
                  </IconButton>

                  <IconButton
                    onMouseDown={handleRotateStart}
                    onMouseUp={handleRotateEnd}
                    sx={rotateButtonSx}
                    aria-label="rotate"
                  >
                    <LoopIcon sx={rotateIconSx} />
                  </IconButton>
                </div>
              </div>
            </Marker>
          )}
          {regridData && (
            <div className="save-home-on-land-container">
              <Button
                disabled={!selectedHomeObject}
                variant="contained"
                color="primary"
                sx={transformNoneSx}
                onClick={handleSaveHomeOnLand}
              >
                Save Home On Land
              </Button>
            </div>
          )}
          {homeModelPlacementError && (
            <HomeModelPlacementToast
              homeModelPlacementError={homeModelPlacementError}
            />
          )}
        </Map>
      </div>
      <Dialog open={isModalOpen} onClose={closeModal}>
        <DialogTitle>Save Home On Land</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            value={savedName}
            onChange={onSavedNameChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button onClick={handleModalSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer />
    </div>
  );
};
